import clsx from 'clsx';
import {
  Fragment,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import debounce from 'just-debounce';

import { DomainBadge } from 'shared/ui/domain-badge';
import { eventService, XApiEventKeys } from 'shared/api/event-service';
import { Asset } from 'shared/api/retail';
import {
  getProgramLink,
  removeDuplicates,
  sortByValues,
  SubjectsMap,
  useEventListener,
  useOnKeyDownOutside,
} from 'shared/lib/utils';

import { getRecommendationEventContextExtension } from '../../../lib';

import {
  DOMAIN_PILL_RIGHT_MARGIN,
  DOMAIN_ROW_HEIGHT,
  MORE_BUTTON_WIDTH,
} from './constants';
import styles from './styles.module.scss';

type RecommendationsItemDomainsProps = {
  domains: string | Record<string, string>;
  isRecommendationCardHovered: boolean;
  isStaticRecommendation: boolean;
  resetMaxRecommendationCardHeight?: () => void;
  subjectsMap?: SubjectsMap;
  userPrograms?: Asset[];
};

export const RecommendationsItemDomains = ({
  domains,
  isRecommendationCardHovered,
  isStaticRecommendation,
  resetMaxRecommendationCardHeight,
  subjectsMap,
  userPrograms,
}: RecommendationsItemDomainsProps) => {
  const domainsWrapperRef = useRef<HTMLDivElement>(null);

  const [isMoreButtonVisible, setIsMoreButtonVisible] =
    useState<boolean>(false);
  const [indexOfMoreBtn, setIndexOfMoreBtn] = useState<number | null>(null);
  const [isMoreBtnHovered, setIsMoreBtnHovered] = useState(false);
  const [totalHeightOfExpandedDomains, setTotalHeightOfExpandedDomains] =
    useState<number | string>(DOMAIN_ROW_HEIGHT);
  const [calculatedWidthsValues, setCalculatedWidthsValues] = useState<{
    domainsContainerWidth: number;
    allDomainsWidthsArr: number[];
    domainsTotalWidth: number;
  } | null>(null);

  // Collapse the domain rows on 'keydown' outside.
  useOnKeyDownOutside(domainsWrapperRef, () => {
    if (isMoreBtnHovered) {
      setIsMoreBtnHovered(false);
      setTotalHeightOfExpandedDomains(DOMAIN_ROW_HEIGHT);
    }
  });

  const handleDomainBadgeClick = async (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    domain: string,
  ) => {
    await eventService.send(
      XApiEventKeys.recommendationProductBreadcrumbSelected,
      undefined,
      {
        result: getRecommendationEventContextExtension(e, domain),
      },
    );
  };

  const uniqueDomains = useMemo(() => {
    if (typeof domains === 'object') {
      return sortByValues(removeDuplicates(domains));
    }
    return domains;
  }, [domains]);

  const getCalculatedWidths = useCallback((excludeMoreBtn = false) => {
    if (!domainsWrapperRef.current) return {};

    const domainsContainerWidth = Math.ceil(
      domainsWrapperRef.current.getBoundingClientRect().width,
    );

    const domainsElementsArr = Array.from(domainsWrapperRef.current.children);

    const moreBtnIndex = domainsElementsArr.findIndex(
      (el) => el.className === styles.moreDomainsBtn,
    );

    if (moreBtnIndex !== -1 && excludeMoreBtn) {
      domainsElementsArr.splice(moreBtnIndex, 1);
    }

    const allDomainsWidthsArr = domainsElementsArr.map(
      (child) =>
        Math.ceil(child.getBoundingClientRect().width) +
        DOMAIN_PILL_RIGHT_MARGIN,
    );

    const domainsTotalWidth = allDomainsWidthsArr.reduce((acc, domainWidth) => {
      return acc + domainWidth;
    }, 0);

    return { domainsContainerWidth, allDomainsWidthsArr, domainsTotalWidth };
  }, []);

  useEventListener(
    'resize',
    debounce(() => {
      const { domainsContainerWidth, allDomainsWidthsArr, domainsTotalWidth } =
        getCalculatedWidths(true);

      if (domainsContainerWidth) {
        setCalculatedWidthsValues({
          domainsContainerWidth,
          allDomainsWidthsArr,
          domainsTotalWidth,
        });

        resetMaxRecommendationCardHeight?.();
      }
    }, 500),
  );

  useEffect(() => {
    const { domainsContainerWidth, allDomainsWidthsArr, domainsTotalWidth } =
      getCalculatedWidths();

    if (domainsContainerWidth) {
      setCalculatedWidthsValues({
        domainsContainerWidth,
        allDomainsWidthsArr,
        domainsTotalWidth,
      });
    }
  }, [getCalculatedWidths]);

  useEffect(() => {
    if (!calculatedWidthsValues) return;

    const { domainsContainerWidth, domainsTotalWidth } = calculatedWidthsValues;

    // If domains don't fit into their container's width, show 'More' button.
    if (domainsTotalWidth > domainsContainerWidth) {
      setIsMoreButtonVisible(true);
    }
  }, [calculatedWidthsValues]);

  useEffect(() => {
    if (!calculatedWidthsValues) return;

    // When 'More' button becomes visible, calculate the position index for it.
    if (isMoreButtonVisible) {
      const { domainsContainerWidth, allDomainsWidthsArr } =
        calculatedWidthsValues;

      let domainsWithMoreBtnWidth = 0;
      let indexNotFitting;

      // Define the index of 'More' button depending on the domains width and container width.
      for (let i = 0; i < allDomainsWidthsArr.length; i++) {
        const calculatedDomainsWithMoreBtnWidth =
          domainsWithMoreBtnWidth + allDomainsWidthsArr[i] + MORE_BUTTON_WIDTH;

        if (calculatedDomainsWithMoreBtnWidth < domainsContainerWidth) {
          domainsWithMoreBtnWidth =
            domainsWithMoreBtnWidth + allDomainsWidthsArr[i];
        } else {
          indexNotFitting = i;
          setIndexOfMoreBtn(indexNotFitting);

          break;
        }
      }
    }
  }, [calculatedWidthsValues, isMoreButtonVisible]);

  useEffect(() => {
    // When the first domain doesn't fit fully along with the "More" button, avoid showing "More" btn.
    if (indexOfMoreBtn === 0) {
      setIndexOfMoreBtn(null);
      setIsMoreButtonVisible(false);
      setTotalHeightOfExpandedDomains('unset');
      resetMaxRecommendationCardHeight?.();
    }
  }, [indexOfMoreBtn, resetMaxRecommendationCardHeight]);

  const handleMoreBtnMouseEnter = () => {
    setIsMoreBtnHovered(true);

    const { domainsContainerWidth, allDomainsWidthsArr } =
      getCalculatedWidths();

    if (domainsContainerWidth && indexOfMoreBtn) {
      // Calculate the domains widths on the rows except for the first one.
      const additionalDomainRowsWidthsArr = allDomainsWidthsArr.slice(
        indexOfMoreBtn + 1,
      );

      // Calculate the total domains' width on the additional rows to define the number of collapsible rows.
      const additionalDomainRowsTotalWidth =
        additionalDomainRowsWidthsArr.reduce((acc, domainWidth) => {
          return acc + domainWidth;
        }, 0);
      const numberOfAdditionalRows = Math.ceil(
        additionalDomainRowsTotalWidth / domainsContainerWidth,
      );

      setTotalHeightOfExpandedDomains(
        (numberOfAdditionalRows + 1) * DOMAIN_ROW_HEIGHT,
      );
    }
  };

  const handleMoreBtnKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.code === 'Space' || e.code === 'Enter' || e.code === 'Tab') {
      handleMoreBtnMouseEnter();
    }
  };

  useEffect(() => {
    // When the recommendation card was unhovered collapse the domain rows.
    if (!isRecommendationCardHovered && isMoreButtonVisible) {
      setIsMoreBtnHovered(false);
      setTotalHeightOfExpandedDomains(DOMAIN_ROW_HEIGHT);
    }
  }, [isMoreButtonVisible, isRecommendationCardHovered]);

  // Static recommendations don't have domain badges.
  if (isStaticRecommendation) {
    return null;
  }

  return (
    <div
      className={styles.recommendationDomains}
      style={{ height: totalHeightOfExpandedDomains }}
      ref={domainsWrapperRef}
    >
      {Object.entries(uniqueDomains).map(([asset, domain], index) => {
        const subject = subjectsMap ? subjectsMap[asset] : undefined;
        const programLink = getProgramLink(asset, userPrograms);

        return indexOfMoreBtn === index ? (
          <Fragment key="withMoreButton">
            <button
              onMouseEnter={handleMoreBtnMouseEnter}
              onKeyDown={handleMoreBtnKeyDown}
              className={clsx(styles.moreDomainsBtn, {
                [styles.isHidden]: isMoreBtnHovered,
              })}
            >
              +{Number(Object.values(uniqueDomains).length) - indexOfMoreBtn}{' '}
              more
            </button>
            <DomainBadge
              onContextMenu={(e) => handleDomainBadgeClick(e, domain)}
              onClick={(e) => handleDomainBadgeClick(e, domain)}
              programLink={programLink}
              programAssetId={asset}
              domain={domain}
              subject={subject}
              domainNameClassNames={styles.domainName}
              domainButtonClassNames={styles.domainBtn}
            />
          </Fragment>
        ) : (
          <DomainBadge
            key={domain}
            onContextMenu={(e) => handleDomainBadgeClick(e, domain)}
            onClick={(e) => handleDomainBadgeClick(e, domain)}
            programLink={programLink}
            programAssetId={asset}
            domain={domain}
            subject={subject}
            domainNameClassNames={styles.domainName}
            domainButtonClassNames={styles.domainBtn}
          />
        );
      })}
    </div>
  );
};
